import React, { useEffect, useRef, useState } from "react";
import {
  Paper,
  Stack,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import EditIcon from "@mui/icons-material/Edit";
import HelpfulDownloads from "../../components/HelpfulDownloads";
import httpClient from "../../_util/api";
import { UploadImagetoFb } from "../../Hooks/uploadImage";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../Store/mainSlice";
import CustomizedSnackbars from '../../shared/Snackbar/SnackBar'

// Document types shown when no attachments exist yet (allows student to attach for the first time)
const DOCUMENT_TYPES = [
  { key: "CNICFrontFile", label: "CNIC Front" },
  { key: "CNICBackFile", label: "CNIC Back" },
  { key: "parentCNICFrontFile", label: "Parent CNIC Front" },
  { key: "parentCNICBackFile", label: "Parent CNIC Back" },
  { key: "proofOfIncomeFile", label: "Proof of Income" },
  { key: "currentElectricBillFile", label: "Current Electric Bill" },
  { key: "additionalDocs", label: "Matric and Intermediate Marksheet" },
  { key: "otherDocumentsFile", label: "Other Documents (Optional)" },
  { key: "referenceLetterFile1", label: "Reference Letter 1" },
  { key: "referenceLetterFile2", label: "Reference Letter 2" },
];

const MyDocuments = () => {
  const [attachments, setAttachments] = useState({});
  const fileInputRef = useRef(null);
  const snackbar_Ref = useRef(null);
  const currentEditingField = useRef(null);
  const dispatch = useDispatch();

  const getAllAttachmentsByCNICNo = async () => {
    try {
      const res = await httpClient.get(`/students/getAllAttachments`);
      if (res?.status === 200) {
        setAttachments(res.data?.attachments ?? {});
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
      const apiMessage = error?.response?.data?.message;
      if (apiMessage && snackbar_Ref.current) {
        snackbar_Ref.current.showMessage("error", apiMessage, "", "i-chk-circle");
      }
    }
  };

  useEffect(() => {
    getAllAttachmentsByCNICNo();
  }, []);

  const openFile = (url, e) => {
    if (url) window.open(url, "_blank");
    e.stopPropagation();
  };

  const triggerFileInput = (fieldKey) => {
    currentEditingField.current = fieldKey;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      snackbar_Ref.current.showMessage("error", "Please upload a PDF file.", "", "i-chk-circle");
      return;
    }

    const MAX_FILE_SIZE_MB = 2;
    
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      alert(`File size should not exceed ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    const field = currentEditingField.current;
    if (!field) return;

    dispatch(showLoader());

    try {
      const uploaded = await UploadImagetoFb({ target: { files: [file] } });
      if (!uploaded?.url || !uploaded?.status) {
        snackbar_Ref.current.showMessage("error", "File upload to storage failed. Please check your connection and try again.", "", "i-chk-circle");
        return;
      }
      const updatedFile = {
        fileName: file.name,
        url: uploaded.url,
      };
      await httpClient.put("/students/updateAttachment", {
        fileKey: field,
        updatedFile,
      });
      snackbar_Ref.current.showMessage("success", "Document attached successfully.", "", "i-chk-circle");
      setAttachments((prev) => ({ ...prev, [field]: updatedFile }));
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      const errMsg = apiMessage || error?.message || "Upload failed. Please try again.";
      snackbar_Ref.current.showMessage("error", errMsg, "", "i-chk-circle");
    } finally {
      dispatch(hideLoader());
      currentEditingField.current = null;
    }
  };
  return (
    <Paper sx={{ width: "100%", overflow: "hidden", padding: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          My Documents
        </Typography>
      </Stack>
       <CustomizedSnackbars ref={snackbar_Ref} />
      <HelpfulDownloads />
      <Stack className="grid-2" justifyContent="space-between">
        {DOCUMENT_TYPES.map(({ key: fileKey, label }) => {
          const fileData = attachments[fileKey];
          return (
          <Stack key={fileKey}>
            <Typography
              variant="h6"
              sx={{ color: "#208146", fontWeight: "bold", mb: 1, textTransform: "capitalize" }}
            >
              {label}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              sx={{
                border: "2px solid #208146",
                height: "54px",
                borderRadius: "11px",
                bgcolor: "white",
                p: 1,
                cursor: fileData?.url ? "pointer" : "default",
              }}
              onClick={(e) => fileData?.url && openFile(fileData.url, e)}
            >
              <InsertDriveFileIcon sx={{ color: "#208146", mr: 1 }} />
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                }}
              >
                {fileData?.fileName || "No file — tap attach to upload"}
              </Typography>
              <IconButton onClick={(e) => { e.stopPropagation(); triggerFileInput(fileKey); }} aria-label="Attach document">
                <EditIcon sx={{ color: "#208146" }} />
              </IconButton>
            </Stack>
          </Stack>
          );
        })}
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </Paper>
  );
};

export default MyDocuments;